// Skills per character (6 classes × 3 characters). Ported from prototypes/match/js/skills.js.
// Targeting kinds drive input: instant · direction (aim) · charge (hold+release) · point (aim) · ally (aim, ground = self)
// · enemy (an enemy hero in range, nothing else).
// `ai` tells the bots (botBrain.ts skillTarget) when a skill is worth using: the situation it is for (`foe`
// range, `near`, `low`, `flee`, `dodge`, `hurt`, `guard`, `brawl`, `brush`, …) plus `cc` (control: wants a follow-up or a fleeing/low target),
// `engage` (closes in: never while disengaging), `aoe` (needs this many enemies in reach), `peel` (…or an
// enemy on us), `vsRanged` (only against a shooter), `shelter` (hp share under which a hit ally in reach is
// covered), `team` (a team buff: wants an ally in reach), `burnFoe` (also cast onto the fight's target).
//
// SKILLS/CAST are intentionally loosely typed (`any` skill defs): the twelve kits are structurally
// different (a charge skill's dmg/range are ranges, not scalars; only some have `r`, `arc`, `cost`...);
// modelling that precisely as a discriminated union would be pure ceremony for data ported verbatim.
import * as THREE from 'three';
import type { Game, Hero, Dash, Hop, Channel, Zone } from './game.js';
import { bulkOf, isHero as isHeroTarget, type Target } from './monsters.js';
import { spawnSummon, summonsOf, golemOf } from './summons.js';
import { bindPlan, bind } from './bind.js';
import type { SkillKey } from './commands.js';
import type { Vec2 } from './events.js';
import { PASSIVES } from './passives.js';
// Only read inside function bodies — safe across the artifacts.ts ↔ game.ts ↔ skills.ts import cycle.
import { maxHpOf } from './artifacts.js';

export const SKILLS = {
  /* ---------- Рыцарь (Страж) ---------- */
  rhino: {
    Q: { cd: 8, targeting: 'direction', range: 8, windup: 0.3, speed: 14, dmg: 35, stun: 0.6, wallStun: 0.6, knock: 9, ai: { foe: [3, 7.5], cc: true, engage: true } },
    W: { cd: 10, targeting: 'instant', duration: 1.5, arc: 1.3, slow: 0.5, empoweredStun: 0.8, healPct: 0.03, ai: { low: 0.6 } },
  },
  durer: {
    Q: { cd: 11, targeting: 'instant', r: 5, taunt: 1.5, ai: { near: 4, cc: true } },
    W: { cd: 12, targeting: 'direction', range: 2.2, half: 2.2, life: 3, ai: { foe: [4, 12], vsRanged: true } },
  },
  landsknecht: {
    Q: { cd: 7, targeting: 'direction', range: 3.3, arc: Math.PI / 2, dmg: 28, knock: 6, ai: { foe: [0, 3] } },
    W: { cd: 14, targeting: 'instant', r: 6, haste: 0.3, life: 3, ai: { near: 9, team: true } },
  },
  /* ---------- Алхимик (Стрелок) ---------- */
  arquebusier: {
    Q: { cd: 7, targeting: 'charge', maxCharge: 1.5, dmg: [18, 38], range: [9, 17], width: 0.9, speed: 32, slow: 0.5, ai: { charge: [5, 14] } },
    W: { cd: 9, targeting: 'instant', away: true, range: 5, hop: 0.45,
         trap: { arm: 0.5, life: 20, dmg: 10, slow: 0.5, slowFor: 2, r: 1.2, sight: 4 }, ai: { flee: 2.5 } },
  },
  astrologer: {
    Q: { cd: 6, targeting: 'direction', range: 10, spread: 0.22, dmg: 15, speed: 22, width: 0.7, ai: { foe: [3, 10] } },
    W: { cd: 13, targeting: 'point', range: 12, r: 8, life: 4, hpPerSec: 0.04, ai: { brush: true, burnFoe: true } },
  },
  salamander: {
    Q: { cd: 8, targeting: 'direction', range: 6, arc: 0.55, life: 1.2, dps: 30, slow: 0.6, ai: { foe: [0, 6] } },
    W: { cd: 10, targeting: 'direction', away: true, range: 5, hop: 0.45, pool: { r: 2.2, life: 3, dps: 12 }, ai: { flee: 3 } },
  },
  /* ---------- Жнец (Дуэлянт) ---------- */
  death: {
    Q: { cd: 7, targeting: 'direction', range: 7, windup: 0.2, speed: 22, dmg: 22, width: 1.2, chainWindow: 3, empowered: 1.5, ai: { foe: [2.5, 6.5], engage: true } },
    W: { cd: 10, targeting: 'direction', range: 3, move: 0.25, invuln: 0.6, ai: { dodge: 0.5 } },
  },
  ferryman: {
    Q: { cd: 9, targeting: 'direction', range: 10, speed: 28, width: 0.9, dmg: 15, stun: 0.35, ai: { foe: [3, 8.5], cc: true, engage: true } },
    W: { cd: 12, targeting: 'instant', r: 5, life: 3, slow: 0.35, ai: { near: 4, aoe: 2, peel: true } },
  },
  devil: {
    Q: { cd: 8, targeting: 'point', range: 7, flight: 0.66, r: 2.5, dmg: 24, slow: 0.3, slowFor: 2.5, ai: { foe: [1.5, 7], engage: true } },
    W: { cd: 14, targeting: 'instant', cost: 0.12, mult: 1.4, life: 4, ai: { near: 3, hpAbove: 0.75, foeBelow: 0.6 } },
  },
  /* ---------- Доктор (Опора) ---------- */
  plague: {
    Q: { cd: 9, targeting: 'point', range: 9, castTime: 0.35, flight: 0.7, r: 2.5, life: 3, heal: 12, dmg: 8, slow: 0.2, ai: { brawl: true } },
    W: { cd: 12, targeting: 'ally', range: 8, castTime: 0.3, shield: 60, life: 3, refund: 0.4, ai: { hurt: 0.6 } },
  },
  monk: {
    Q: { cd: 8, targeting: 'ally', range: 9, castTime: 0.3, total: 55, life: 4, ai: { hurt: 0.7 } },
    W: { cd: 12, targeting: 'instant', castTime: 0.35, r: 3.5, life: 4, ai: { near: 5, shelter: 0.6 } },
  },
  grotesque: {
    Q: { cd: 6, targeting: 'direction', range: 9, castTime: 0.35, r: 2.2, life: 4, speed: 2.2, dps: 12, ai: { foe: [2, 9] } },
    W: { cd: 10, targeting: 'instant', castTime: 0.25, r: 3.5, dmg: 10, knock: 8, ai: { near: 3, aoe: 2, peel: true } },
  },
  /* ---------- Астрал (Геймченджер) ---------- */
  shadow: {
    // `cover`: castable only in grass or out of the enemy's sight (Game.inCover), which is also what holds it.
    Q: { cd: 12, targeting: 'instant', cover: true, life: 3, ambush: 3, ambushHits: 2, ambushWindow: 3, regenPct: 0.015, ai: { stealth: 9 } },
    // No `ai` hint: Game.shadowThink decides when to cast it (escape home when hurt, finish a wounded foe).
    // `r`/`dmg`: the swap hurts every hostile within `r` of the spot she leaves and of the one she lands on — once.
    W: { cd: 5, targeting: 'point', range: 8, delay: 1, r: 2, dmg: 10 },
  },
  sin: {
    Q: { cd: 12, targeting: 'enemy', range: 7, castTime: 0.2, life: 2, drain: 0.01, ai: { foe: [2, 7], cc: true } },
    W: { cd: 14, targeting: 'instant', castTime: 0.2, r: 6, life: 4, share: 0.5, taken: 0.35, ai: { guard: 0.7 } },
  },
  nightmare: {
    Q: { cd: 13, targeting: 'point', range: 8, flight: 0.6, r: 3, fear: 1.5, ai: { foe: [2, 8], cc: true, engage: true } },
    W: { cd: 16, targeting: 'instant', castTime: 0.25, r: 5, life: 3, chance: 0.5, mult: 0.5, ai: { near: 5, aoe: 2 } },
  },
  /* ---------- Заклинатели (Призыватель): the creatures themselves live in summons.ts ---------- */
  birdcatcher: {
    // `peckDelay`: each peck that lands on a hero mid-windup (a cast, Таран's or Выпад's windup) pushes it back this much.
    Q: { cd: 14, targeting: 'instant', castTime: 0.25, count: 3, life: 6, peckDelay: 0.2, ai: { near: 8 } },
    W: { cd: 12, targeting: 'enemy', range: 10, castTime: 0.2, speed: 7.41, dmg: 20, slow: 0.45, slowFor: 1.5, ai: { foe: [2, 10] } },
  },
  puppeteer: {
    Q: { cd: 16, targeting: 'point', range: 6, castTime: 0.4, life: 10, ai: { golem: 9 } },
    // Thrown at the ground: every enemy hero in `r` of the spot is tied to the nearest anchor there (bind.ts).
    W: { cd: 12, targeting: 'point', range: 8, castTime: 0.2, r: 2.5, life: 2.5, ai: { bind: true } },
  },
  shewolf: {
    Q: { cd: 14, targeting: 'instant', castTime: 0.25, count: 2, life: 8, ai: { near: 8 } },
    // Wolf form: her basic attack turns melee at `range`; the speed and damage are ordinary haste/dmg buffs.
    W: { cd: 14, targeting: 'instant', life: 5, haste: 0.4, mult: 1.3, range: 1.8, ai: { foe: [0, 6], engage: true } },
  },
} as const satisfies Record<string, Record<SkillKey, any>>;

const v3 = (x: number, z: number) => new THREE.Vector3(x, 0, z);
const flatDist = (a: { x: number; z: number }, b: { x: number; z: number }) => Math.hypot(a.x - b.x, a.z - b.z);
const isHero = (t: unknown): t is Hero => !!t && typeof t === 'object' && 'alive' in (t as object) && !('den' in (t as object)) && !('owner' in (t as object));
const toPoint = (t: Hero | Vec2): THREE.Vector3 => (isHero(t) ? t.pos.clone() : v3(t.x, t.z));
function dirTo(h: Hero, point: THREE.Vector3) {
  const d = point.clone().sub(h.pos); d.y = 0;
  if (d.lengthSq() < 1e-4) d.set(Math.sin(h.yaw), 0, Math.cos(h.yaw));
  return d.normalize();
}
const face = (h: Hero, dir: THREE.Vector3) => { h.wantYaw = Math.atan2(dir.x, dir.z); h.snapTurn = true; };
const rotY = (d: THREE.Vector3, a: number) => new THREE.Vector3(d.x * Math.cos(a) + d.z * Math.sin(a), 0, -d.x * Math.sin(a) + d.z * Math.cos(a));
function walkableLen(g: Game, from: THREE.Vector3, dir: THREE.Vector3, max: number) {
  let len = 0;
  while (len < max && g.nav.isWalkable(from.x + dir.x * (len + 0.25), from.z + dir.z * (len + 0.25))) len += 0.25;
  return len;
}
/* Enter 'casting', play the cast clip(s), run fn after `time` (skills.js's castThen). */
function castThen(g: Game, h: Hero, clip: string | string[], time: number, fn: () => void) {
  h.cast = { time, fn };
  g.setState(h, 'casting');
  g.emitAnim(h.id, ([] as string[]).concat(clip), 0.08);
}
const inConeAt = (h: Hero, p: { x: number; z: number }, dir: THREE.Vector3, r: number, arc: number) => {
  const d = new THREE.Vector3(p.x - h.pos.x, 0, p.z - h.pos.z);
  const len = d.length();
  return len < r && (len < 0.6 || d.normalize().dot(dir) > Math.cos(arc));
};
const inCone = (h: Hero, e: { pos: THREE.Vector3 }, dir: THREE.Vector3, r: number, arc: number) => inConeAt(h, e.pos, dir, r, arc);
/* `resetBase` always fires here, clip or not — ported from the prototype's unconditional
   `h.rig.base = 'Idle'` write, which ran even when recover() had no clip to play. It has to travel as
   an event rather than being derived from hero.state in the view: updateSkill's 'brace' branch calls
   recover() and then immediately setState(h, 'normal') in the same tick, so 'recover' is sometimes
   never an observable hero.state at all. */
function recover(g: Game, h: Hero, time: number, clip?: string) {
  g.setState(h, 'recover');
  h.recoverFor = time;
  g.emitAnim(h.id, clip ? [clip] : [], 0.05, { resetBase: true });
}

/* ---------------- casting ---------------- */
const CAST: Record<string, Record<SkillKey, (g: Game, h: Hero, sk: any, target?: Hero | Vec2 | null) => 'nocd' | void>> = {
  rhino: {
    Q(g, h, sk, target) {
      const dir = dirTo(h, toPoint(target as Hero | Vec2));
      h.dash = { dir, left: Math.min(sk.range, flatDist(h.pos, toPoint(target as Hero | Vec2)) + 0.6) };
      face(h, dir);
      g.setState(h, 'windup');
      g.emitAnim(h.id, ['ChargeWindup'], 0.08);
    },
    W(g, h) { h.braceArmed = false; g.setState(h, 'brace'); g.emitAnim(h.id, ['Brace'], 0.1); },
  },
  durer: {
    Q(g, h, sk) {
      castThen(g, h, ['Taunt', 'BraceBash'], 0.25, () => {
        for (const e of g.enemiesOf(h)) if (flatDist(e.pos, h.pos) < sk.r) g.taunt(e, h, sk.taunt);
        g.emit({ type: 'fx', name: 'rally', hero: h.id });
      });
    },
    W(g, h, sk, target) {
      const dir = dirTo(h, toPoint(target as Hero | Vec2));
      face(h, dir);
      castThen(g, h, ['PlantShield', 'Brace'], 0.3, () => {
        g.barriers.push({ center: h.pos.clone().addScaledVector(dir, sk.range), dir, half: sk.half, team: h.team, until: g.t + sk.life });
      });
    },
  },
  landsknecht: {
    Q(g, h, sk, target) {
      const dir = dirTo(h, toPoint(target as Hero | Vec2));
      face(h, dir);
      castThen(g, h, ['Sweep', 'Attack2'], 7 / 24, () => {
        g.emit({ type: 'fx', name: 'sweep', hero: h.id, dir: g.pos2(dir) });
        for (const e of g.hostilesOf(h)) {
          const seen = g.rewind(h, e);
          if (!seen.alive || !inConeAt(h, seen, dir, sk.range + bulkOf(e), sk.arc)) continue;
          const out = v3(seen.x, seen.z).sub(h.pos).setY(0).normalize();
          g.damage(e, sk.dmg, h, { knock: out.multiplyScalar(sk.knock), skill: true });
        }
      });
    },
    W(g, h, sk) {
      castThen(g, h, ['Warcry', 'BraceBash'], 0.2, () => {
        for (const a of g.heroes) if (a.alive && a.team === h.team && flatDist(a.pos, h.pos) < sk.r) g.buff(a, 'haste', sk.haste, sk.life);
        g.emit({ type: 'fx', name: 'warcry', hero: h.id });
      });
    },
  },
  arquebusier: {
    Q(g, h, sk, target) {
      h.charge = { t0: g.t, dir: dirTo(h, toPoint(target as Hero | Vec2)) };  // cooldown starts on release
      face(h, h.charge.dir);
      g.setState(h, 'charging');
      g.emitAnim(h.id, ['AimCharge'], 0.1);
      return 'nocd';
    },
    W(g, h, sk, target) {
      // a player's instant cast hops straight back from where the hero faces; bots pass the foe to flee from
      const from = hopAway(g, h, sk, target ?? { x: h.pos.x + Math.sin(h.yaw), z: h.pos.z + Math.cos(h.yaw) });
      g.traps.push({ pos: from, team: h.team, src: h, armAt: g.t + sk.trap.arm, until: g.t + sk.trap.life, ...sk.trap });
    },
  },
  astrologer: {
    Q(g, h, sk, target) {
      const dir = dirTo(h, toPoint(target as Hero | Vec2));
      face(h, dir);
      // three shots on the clip's recoil frames 5, 7, 9, sweeping left → right
      castThen(g, h, ['Volley', 'Attack'], 5 / 24, () => {
        [-sk.spread, 0, sk.spread].forEach((a: number, n: number) => g.later(n * 2 / 24, () => {
          if (!h.alive) return;
          g.spawnLinear({ src: h, dir: rotY(dir, a), speed: sk.speed, range: sk.range, width: sk.width, dmg: sk.dmg, color: 0xb8c8ff, look: 'star' });
          g.emit({ type: 'fx', name: 'muzzle', hero: h.id, big: n === 1 });
        }));
      });
    },
    W(g, h, sk, target) {
      const p = toPoint(target as Hero | Vec2), d = dirTo(h, p), len = Math.min(sk.range, flatDist(h.pos, p));
      const at = h.pos.clone().addScaledVector(d, len);
      face(h, d);
      castThen(g, h, ['Divine', 'Attack'], 0.3, () => {
        g.reveals.push({ x: at.x, z: at.z, r: sk.r, team: h.team, until: g.t + sk.life, t0: g.t, src: h, hpPerSec: sk.hpPerSec, tickAt: g.t });
      });
    },
  },
  salamander: {
    Q(g, h, sk, target) {
      h.channel = { dir: dirTo(h, toPoint(target as Hero | Vec2)), until: g.t + sk.life, tickAt: g.t };
      face(h, h.channel.dir);
      g.setState(h, 'channel');
      g.emitAnim(h.id, ['Flame', 'AimCharge'], 0.08);
    },
    W(g, h, sk, target) {
      const from = hopAway(g, h, sk, target as Hero | Vec2);
      g.pools.push({ pos: from, team: h.team, src: h, r: sk.pool.r, until: g.t + sk.pool.life, life: sk.pool.life, dmg: sk.pool.dps, look: 'fire', tickAt: g.t, burn: true });
    },
  },
  death: {
    Q(g, h, sk, target) {
      const empowered = h.feintArmed;
      h.feintArmed = false;
      const dir = dirTo(h, toPoint(target as Hero | Vec2));
      h.dash = { dir, left: sk.range, hit: new Set(), mult: empowered ? sk.empowered : 1, from: h.pos.clone() };
      face(h, dir);
      g.setState(h, 'lungeWindup');
      g.emitAnim(h.id, ['LungeWindup'], 0.06);
      if (empowered) return 'nocd';
    },
    W(g, h, sk, target) {
      const dir = dirTo(h, toPoint(target as Hero | Vec2)), len = walkableLen(g, h.pos, dir, sk.range);
      h.hop = { from: h.pos.clone(), to: h.pos.clone().addScaledVector(dir, len), dur: sk.move };
      h.invulnUntil = Math.max(h.invulnUntil, g.t + sk.invuln);  // never cuts a longer Щит Мученика short
      g.setState(h, 'feint');
      g.emitAnim(h.id, ['Feint'], 0.05);
    },
  },
  ferryman: {
    Q(g, h, sk, target) {
      const dir = dirTo(h, toPoint(target as Hero | Vec2));
      face(h, dir);
      castThen(g, h, ['HookThrow', 'LungeWindup'], 0.2, () => {
        g.emitAnim(h.id, ['LungeStrike'], 0.05, { unless: ['HookThrow'] });
        g.spawnLinear({ src: h, dir, speed: sk.speed, range: sk.range, width: sk.width, dmg: sk.dmg, color: 0x8a8580, look: 'hook',
          onHit: (e) => {
            g.damage(e, sk.dmg, h, { stun: sk.stun, skill: true });
            if (!isHeroTarget(e)) return;  // a monster or a summon is never dragged
            // Resolved against the same rewound point the projectile hit test used, not wherever the
            // target has since moved to — the pull is part of that one hit, not a fresh present-frame read.
            const seen = g.rewind(h, e), from = v3(seen.x, seen.z);
            const full = h.pos.clone().addScaledVector(dirTo(h, from), 1.4);
            // Толстая шкура: the drag covers only part of the way, over the same duration.
            const to = e.kind === 'rhino' ? from.clone().lerp(full, PASSIVES.rhino.mult) : full;
            if (g.nav.isWalkable(to.x, to.z)) {
              e.pull = { from, to, t0: g.t, dur: 0.3, by: h };
              if (to !== full) g.stats.passive(e.id, flatDist(to, full));
            }
          } });
      });
    },
    W(g, h, sk) {
      castThen(g, h, ['Lantern', 'Attack2'], 0.2, () => {
        g.pools.push({ pos: h.pos.clone(), follow: h, team: h.team, src: h, r: sk.r, until: g.t + sk.life, life: sk.life, slow: sk.slow, reveal: true, look: 'lantern', tickAt: g.t });
      });
    },
  },
  devil: {
    Q(g, h, sk, target) {
      const p = toPoint(target as Hero | Vec2), d = dirTo(h, p), len = Math.min(sk.range, flatDist(h.pos, p));
      const want = h.pos.clone().addScaledVector(d, len), land = g.nav.nearestWalkable(want.x, want.z);
      h.hop = { from: h.pos.clone(), to: v3(land.x, land.z), dur: sk.flight, arc: 1.8 };
      face(h, d);
      g.setState(h, 'leap');
      g.emitAnim(h.id, ['Leap', 'Lunge'], 0.05);
    },
    W(g, h, sk) {
      castThen(g, h, ['Pact', 'Hit'], 0.15, () => {
        h.hp = Math.max(1, h.hp - h.def.hp * sk.cost);
        g.buff(h, 'dmg', sk.mult, sk.life, false);
        g.emit({ type: 'fx', name: 'pact', hero: h.id });
      });
    },
  },
  plague: {
    Q(g, h, sk, target) {
      const p = toPoint(target as Hero | Vec2), d = dirTo(h, p), len = Math.min(sk.range, flatDist(h.pos, p));
      const at = h.pos.clone().addScaledVector(d, len);
      face(h, d);
      castThen(g, h, 'CastQ', sk.castTime, () => {
        g.spawnLob({ src: h, to: at, flight: sk.flight, r: sk.r, onLand: (pt) => {
          g.pools.push({ pos: pt.clone(), team: h.team, src: h, r: sk.r, until: g.t + sk.life, life: sk.life, heal: sk.heal, dmg: sk.dmg, slow: sk.slow, look: 'heal', tickAt: g.t });
          g.emit({ type: 'fx', name: 'shatter', pos: g.pos2(pt) });
        } });
      });
    },
    W(g, h, sk, target) {
      const ally = allyTarget(g, h, sk, target);
      if (ally !== h) face(h, dirTo(h, ally.pos));
      castThen(g, h, 'CastW', sk.castTime, () => {
        if (!ally.alive) return;
        const P = PASSIVES.plague, amount = ally.hp < maxHpOf(ally) * P.below ? sk.shield * (1 + P.bonus) : sk.shield;  // Сортировка
        ally.shield = { amount, until: g.t + sk.life, src: h };
        g.stats.shielded(h.id, amount);
        g.stats.passive(h.id, amount - sk.shield);
        ally.slows = [];
        g.emit({ type: 'fx', name: 'ward', hero: ally.id });
      });
    },
  },
  monk: {
    Q(g, h, sk, target) {
      const ally = allyTarget(g, h, sk, target);
      if (ally !== h) face(h, dirTo(h, ally.pos));
      castThen(g, h, ['Pray', 'CastW'], sk.castTime, () => {
        if (!ally.alive) return;
        ally.hots.push({ perSec: sk.total / sk.life, until: g.t + sk.life, src: h });
        g.emit({ type: 'fx', name: 'prayer', hero: ally.id });
      });
    },
    W(g, h, sk) {
      castThen(g, h, ['Blessing', 'CastQ'], sk.castTime, () => {
        g.pools.push({ pos: h.pos.clone(), team: h.team, src: h, r: sk.r, until: g.t + sk.life, life: sk.life, immune: true, look: 'holy', tickAt: g.t, tickAt0: g.t });
      });
    },
  },
  grotesque: {
    Q(g, h, sk, target) {
      const dir = dirTo(h, toPoint(target as Hero | Vec2));
      face(h, dir);
      castThen(g, h, 'CastQ', sk.castTime, () => {
        g.pools.push({ pos: h.pos.clone().addScaledVector(dir, 1.5), vel: dir.clone().multiplyScalar(sk.speed), team: h.team, src: h,
                       r: sk.r, until: g.t + sk.life, life: sk.life, dmg: sk.dps, look: 'cloud', tickAt: g.t });
      });
    },
    W(g, h, sk) {
      castThen(g, h, ['Flap', 'CastW'], sk.castTime, () => {
        for (const e of g.hostilesOf(h)) if (flatDist(e.pos, h.pos) < sk.r + bulkOf(e)) {
          const out = e.pos.clone().sub(h.pos).setY(0);
          if (out.lengthSq() < 1e-4) out.set(1, 0, 0);
          g.damage(e, sk.dmg, h, { knock: out.normalize().multiplyScalar(sk.knock), skill: true });
        }
        g.emit({ type: 'fx', name: 'feathers', pos: g.pos2(h.pos) });
      });
    },
  },
  shadow: {
    Q(g, h, sk) {
      h.stealthUntil = g.t + sk.life;
      g.emit({ type: 'fx', name: 'stealth', hero: h.id });
      g.emitAnim(h.id, ['CastQ', 'Attack'], 0.08);
    },
    W(g, h, sk, target) {
      const p = toPoint(target as Hero | Vec2), d = dirTo(h, p), len = Math.min(sk.range, flatDist(h.pos, p));
      const want = h.pos.clone().addScaledVector(d, len), at = g.nav.nearestWalkable(want.x, want.z);
      const mark: Zone = { pos: v3(at.x, at.z), team: h.team, src: h, r: 0.8, until: g.t + sk.delay, life: sk.delay, look: 'shadowMark', tickAt: g.t, hideWithSrc: true };
      g.pools.push(mark);
      h.wallShadow = mark;
      face(h, d);
      g.emitAnim(h.id, ['CastW', 'Attack'], 0.08);
      scheduleShadowSwap(g, h, mark, sk.delay);
    },
  },
  sin: {
    Q(g, h, sk, target) {
      const foe = target as Hero;
      face(h, dirTo(h, foe.pos));
      castThen(g, h, ['CastQ', 'Attack'], sk.castTime, () => {
        if (foe.alive && flatDist(h.pos, foe.pos) <= sk.range + 1) g.force(foe, h, 'charm', sk.life, sk.drain);
      });
    },
    W(g, h, sk) {
      castThen(g, h, ['CastW', 'Attack'], sk.castTime, () => {
        g.pools.push({ pos: h.pos.clone(), follow: h, team: h.team, src: h, r: sk.r, until: g.t + sk.life, life: sk.life, share: sk.share, taken: sk.taken, look: 'atonement', tickAt: g.t });
        g.emit({ type: 'fx', name: 'atonement', hero: h.id });
      });
    },
  },
  nightmare: {
    Q(g, h, sk, target) {
      const p = toPoint(target as Hero | Vec2), d = dirTo(h, p), len = Math.min(sk.range, flatDist(h.pos, p));
      const want = h.pos.clone().addScaledVector(d, len), land = g.nav.nearestWalkable(want.x, want.z);
      h.hop = { from: h.pos.clone(), to: v3(land.x, land.z), dur: sk.flight, arc: 1.2 };
      h.invulnUntil = Math.max(h.invulnUntil, g.t + sk.flight);
      face(h, d);
      g.setState(h, 'flock');
      g.emitAnim(h.id, ['Flock', 'Leap', 'CastQ'], 0.05);
    },
    W(g, h, sk) {
      castThen(g, h, ['CastW', 'Attack'], sk.castTime, () => {
        g.pools.push({ pos: h.pos.clone(), follow: h, team: h.team, src: h, r: sk.r, until: g.t + sk.life, life: sk.life, confuse: true, look: 'delusion', tickAt: g.t });
        g.emit({ type: 'fx', name: 'delusion', hero: h.id });
      });
    },
  },
  birdcatcher: {
    Q(g, h, sk) {
      castThen(g, h, ['CastQ', 'Attack'], sk.castTime, () => {
        for (const old of summonsOf(g, h, 'magpie')) old.until = g.t;  // a new flock replaces the old one
        spawnAround(g, h, 'magpie', sk.count, sk.life, 0.8);
      });
    },
    W(g, h, sk, target) {
      const foe = target as Hero;
      face(h, dirTo(h, foe.pos));
      castThen(g, h, ['CastW', 'Attack'], sk.castTime, () => {
        if (!foe.alive) return;
        g.projectiles.push({ kind: 'homing', src: h, target: foe, pos: new THREE.Vector3(h.pos.x, 1.8, h.pos.z), speed: sk.speed, dmg: sk.dmg,
          color: 0xc8a878, look: 'falcon', live: true, onHit: (e) => { g.damage(e, sk.dmg, h, { slow: sk.slow, slowFor: sk.slowFor, skill: true }); } });
      });
    },
  },
  puppeteer: {
    Q(g, h, sk, target) {
      const p = toPoint(target as Hero | Vec2), d = dirTo(h, p), len = Math.min(sk.range, flatDist(h.pos, p));
      const want = h.pos.clone().addScaledVector(d, len), at = g.nav.nearestWalkable(want.x, want.z);
      face(h, d);
      castThen(g, h, ['CastQ', 'Attack'], sk.castTime, () => {
        const old = golemOf(g, h);
        if (old) old.until = g.t;  // one golem at a time: the old one crumbles (expires — no Мастер shield)
        spawnSummon(g, h, 'golem', at, sk.life, 0);
      });
    },
    W(g, h, sk, target) {
      const p = toPoint(target as Hero | Vec2), d = dirTo(h, p), len = Math.min(sk.range, flatDist(h.pos, p));
      const at = h.pos.clone().addScaledVector(d, len);
      face(h, d);
      castThen(g, h, ['CastW', 'Attack'], sk.castTime, () => {
        g.emit({ type: 'fx', name: 'bindThrow', hero: h.id, to: g.pos2(at) });
        g.emit({ type: 'fx', name: 'bind', pos: g.pos2(at) });
        for (const { a, b } of bindPlan(g, h, at, sk.r)) bind(g, a, b, sk.life);  // none: a miss, the cooldown spent
      });
    },
  },
  shewolf: {
    Q(g, h, sk) {
      castThen(g, h, ['CastQ', 'Attack'], sk.castTime, () => {
        for (const old of summonsOf(g, h, 'wolf')) old.until = g.t;
        spawnAround(g, h, 'wolf', sk.count, sk.life, 1.2);
      });
    },
    W(g, h, sk) {
      h.wolfUntil = g.t + sk.life;
      g.buff(h, 'haste', sk.haste, sk.life, false);
      g.buff(h, 'dmg', sk.mult, sk.life, false);
      g.emit({ type: 'fx', name: 'wolfForm', hero: h.id });
      g.emitAnim(h.id, ['CastW', 'Attack'], 0.08);
    },
  },
};

/* `count` creatures in a ring `r` around the hero, the first one behind her. */
function spawnAround(g: Game, h: Hero, kind: 'magpie' | 'wolf', count: number, life: number, r: number) {
  for (let i = 0; i < count; i++) {
    const a = h.yaw + Math.PI + (i - (count - 1) / 2) * 1.1;
    spawnSummon(g, h, kind, { x: h.pos.x + Math.sin(a) * r, z: h.pos.z + Math.cos(a) * r }, life, i);
  }
}

function hopAway(g: Game, h: Hero, sk: any, target: Hero | Vec2) {
  const away = dirTo(h, toPoint(target)).negate(), from = h.pos.clone();
  h.hop = { from, to: from.clone().addScaledVector(away, walkableLen(g, from, away, sk.range)), dur: sk.hop, arc: 0.9 };
  face(h, away.clone().negate());
  g.setState(h, 'hop');
  g.emitAnim(h.id, ['Retreat'], 0.06);
  return from;
}
function allyTarget(g: Game, h: Hero, sk: any, target?: Hero | Vec2 | null): Hero {
  return isHero(target) && target.team === h.team && target.alive && flatDist(h.pos, target.pos) <= sk.range ? target : h;
}

/* Тень на стене's swap onto `mark` in `delay` seconds, unless she died or cast a newer mark first. Exported
   for the client's prediction (net/predictedGame.ts), which re-arms a swap already pending on the server. */
export function scheduleShadowSwap(g: Game, h: Hero, mark: Zone, delay: number) {
  g.later(delay, () => {
    if (!h.alive || h.wallShadow !== mark) return;
    h.wallShadow = null;
    // A teleport, not a walk: whatever was moving her (a drag, a slide, a hop) ends here.
    if (h.state !== 'normal' && h.state !== 'recover') g.interrupt(h);
    h.pull = null; h.kb.set(0, 0, 0); h.path = [];
    const from = h.pos.clone();
    h.pos.set(mark.pos.x, 0, mark.pos.z);
    h.stillSince = g.t;  // a move of her own (Без отражения), though no walk
    // Destination only: where she left from may be a spot the enemy never saw her at.
    g.emit({ type: 'fx', name: 'shadowSwap', hero: h.id, pos: g.pos2(h.pos) });
    // Both ends burst; the one at her old spot as a bare spot, like the rest of the departure
    const sk = SKILLS.shadow.W;
    for (const e of g.hostilesOf(h)) {
      const reach = sk.r + bulkOf(e);
      if (flatDist(e.pos, from) < reach || flatDist(e.pos, h.pos) < reach) g.damage(e, sk.dmg, h, { skill: true });
    }
    for (const at of [from, h.pos]) g.emit({ type: 'fx', name: 'shadowBurst', pos: g.pos2(at) });
  });
}

export function castSkill(g: Game, h: Hero, key: SkillKey, target: Hero | Vec2 | null): boolean {
  const sk = SKILLS[h.kind]?.[key];
  if (!sk || !g.canCast(h, key)) return false;
  if (sk.targeting !== 'instant' && sk.targeting !== 'ally' && !target) return false;
  // 'enemy': a live enemy hero in range, or nothing — no ground, no monster, no cooldown spent on a miss.
  if (sk.targeting === 'enemy' && !(isHero(target) && target.alive && target.team !== h.team && !g.silhouette(target) && flatDist(h.pos, target.pos) <= (sk as { range: number }).range)) return false;
  g.stir(h);
  h.revealUntil = g.t + 1.5;
  h.pending = null;
  h.pendingTotem = null;
  h.path = [];
  if (CAST[h.kind]![key](g, h, sk, target) !== 'nocd') h.cd[key] = sk.cd;
  return true;
}

/* Charge (Аркебузир) and channel (Саламандра): input or a bot steers while it lasts. */
export function aimCharge(g: Game, h: Hero, point: THREE.Vector3 | null) {
  if (!point) return;
  if (h.state === 'charging' && h.charge) { h.charge.dir = dirTo(h, point); face(h, h.charge.dir); }
  if (h.state === 'channel' && h.channel) { h.channel.dir = dirTo(h, point); face(h, h.channel.dir); }
}
export function chargeLevel(g: Game, h: Hero) {
  return h.state === 'charging' && h.charge ? Math.min(1, (g.t - h.charge.t0) / SKILLS.arquebusier.Q.maxCharge) : 0;
}
export function releaseCharge(g: Game, h: Hero) {
  if (h.state !== 'charging' || !h.charge) return;
  const sk = SKILLS.arquebusier.Q, k = chargeLevel(g, h);
  const dmg = sk.dmg[0] + (sk.dmg[1] - sk.dmg[0]) * k, range = sk.range[0] + (sk.range[1] - sk.range[0]) * k;
  h.cd.Q = sk.cd;
  g.spawnLinear({ src: h, dir: h.charge.dir, speed: sk.speed, range, width: sk.width * (0.8 + 0.5 * k), dmg, color: 0xff9a3c, big: k > 0.6, look: 'lance' });
  g.emit({ type: 'fx', name: 'muzzle', hero: h.id, big: true });
  h.charge = null;
  recover(g, h, 0.3, 'AimRelease');
}

/* ---------------- per-frame skill states; returns true when the state owns the hero this frame ---------------- */
export function updateSkill(g: Game, h: Hero, dt: number): boolean {
  const s = h.state;
  if (s === 'normal' || s === 'charging') return false;
  if (s === 'brace') {
    const sk = SKILLS.rhino.W;
    if (h.stateT >= sk.duration) {
      recover(g, h, 0, 'BraceBash');
      g.setState(h, 'normal');
      if (h.braceArmed) { h.empowered = true; g.emit({ type: 'fx', name: 'armed', hero: h.id }); }
    }
    return false;
  }
  if (s === 'recover') { if (h.stateT >= h.recoverFor) g.setState(h, 'normal'); return true; }
  if (s === 'casting') {
    if (h.cast && h.stateT >= h.cast.time) {
      const fn = h.cast.fn;
      h.cast = null;
      g.setState(h, 'normal');
      fn();
    }
    return true;
  }

  if (s === 'windup') {  // Таран
    if (h.stateT >= SKILLS.rhino.Q.windup) { g.setState(h, 'dash'); g.emitAnim(h.id, ['Charge'], 0.06); }
    return true;
  }
  if (s === 'dash') {
    const sk = SKILLS.rhino.Q, dash = h.dash as Dash, step = Math.min(dash.left, sk.speed * dt);
    const nx = h.pos.x + dash.dir.x * step, nz = h.pos.z + dash.dir.z * step;
    const wall = !g.nav.isWalkable(nx, nz);
    if (!wall) { h.pos.set(nx, 0, nz); dash.left -= step; }
    g.emit({ type: 'fx', name: 'dust', hero: h.id });
    const victim = g.hostilesOf(h).find(e => { const seen = g.rewind(h, e); return seen.alive && flatDist(seen, h.pos) < 1.35 + bulkOf(e); });
    if (victim || wall || dash.left <= 0.01) {
      if (victim) {
        // The hit was decided against the rewound position; the wall-slam geometry and its fx must
        // resolve in that same frame of reference, not wherever the victim has since moved to.
        const seen = g.rewind(h, victim);
        const slam = g.wallBehind(seen, dash.dir, 2.6);
        g.damage(victim, sk.dmg, h, { stun: sk.stun + (slam ? sk.wallStun : 0), knock: dash.dir.clone().multiplyScalar(sk.knock), skill: true });
        g.emit({ type: 'fx', name: 'ramHit', pos: { x: seen.x, z: seen.z }, big: slam }, victim);
      }
      h.dash = null;
      recover(g, h, 0.5, 'ChargeBash');
    }
    return true;
  }
  if (s === 'hop' || s === 'feint' || s === 'leap' || s === 'flock') {
    const hop = h.hop as Hop, k = Math.min(1, h.stateT / hop.dur);
    h.pos.lerpVectors(hop.from, hop.to, k);
    h.lift = hop.arc ? Math.sin(k * Math.PI) * hop.arc : 0;
    if (s === 'feint' || s === 'leap' || s === 'flock') g.emit({ type: 'fx', name: 'echo', hero: h.id });
    if (k >= 1) {
      h.lift = 0;
      h.hop = null;  // the hop is over as of this tick (h.pos is already at hop.to) — must not linger for the server's snapshot to keep rebroadcasting hop.from
      if (s === 'leap') {
        const sk = SKILLS.devil.Q;
        for (const e of g.hostilesOf(h)) {
          const seen = g.rewind(h, e);
          if (seen.alive && flatDist(seen, h.pos) < sk.r + bulkOf(e)) g.damage(e, sk.dmg, h, { slow: sk.slow, slowFor: sk.slowFor, skill: true });
        }
        g.emit({ type: 'fx', name: 'leapLand', pos: g.pos2(h.pos) });
        recover(g, h, 0.3);
        g.emitAnim(h.id, ['LungeStrike'], 0.05, { unless: ['Leap'] });  // skip if the model has a dedicated Leap clip
      } else if (s === 'flock') {
        const sk = SKILLS.nightmare.Q;
        for (const e of g.enemiesOf(h)) if (flatDist(e.pos, h.pos) < sk.r) g.force(e, h, 'fear', sk.fear);
        g.emit({ type: 'fx', name: 'flockLand', pos: g.pos2(h.pos) });
        recover(g, h, 0.2);
      } else g.setState(h, 'normal');
    }
    return true;
  }
  if (s === 'lungeWindup') {
    if (h.stateT >= SKILLS.death.Q.windup) { g.setState(h, 'lunge'); g.emitAnim(h.id, ['Lunge'], 0.05); }
    return true;
  }
  if (s === 'lunge') {
    const sk = SKILLS.death.Q, dash = h.dash as Dash, step = Math.min(dash.left, sk.speed * dt);
    const nx = h.pos.x + dash.dir.x * step, nz = h.pos.z + dash.dir.z * step;
    const wall = !g.nav.isWalkable(nx, nz);
    if (!wall) { h.pos.set(nx, 0, nz); dash.left -= step; }
    g.emit({ type: 'fx', name: 'echo', hero: h.id });
    for (const e of g.hostilesOf(h)) {  // passes THROUGH: every enemy on the path is cut once
      const hit = dash.hit as Set<Target>;
      if (hit.has(e)) continue;
      const seen = g.rewind(h, e);
      if (!seen.alive || flatDist(seen, h.pos) > sk.width + bulkOf(e)) continue;
      hit.add(e);
      g.damage(e, sk.dmg * (dash.mult ?? 1), h, { skill: true });
      g.emit({ type: 'fx', name: 'splat', pos: { x: seen.x, z: seen.z } }, e);  // where the shot connected, not where the target now is
    }
    if (wall || dash.left <= 0.01) {
      g.emit({ type: 'fx', name: 'cut', from: g.pos2(dash.from as THREE.Vector3), to: g.pos2(h.pos) });
      if ((dash.hit as Set<Target>).size && !(g.t - (h.chainAt ?? -99) < sk.chainWindow)) { h.cd.Q = 0; h.chainAt = g.t; }  // a hit resets Выпад once
      h.dash = null;
      recover(g, h, 0.35, 'LungeStrike');
    }
    return true;
  }
  if (s === 'channel') {  // Огненная струя
    const sk = SKILLS.salamander.Q, c = h.channel as Channel;
    if (g.t >= c.tickAt) {
      c.tickAt = g.t + 0.15;
      g.emit({ type: 'fx', name: 'flame', hero: h.id, dir: g.pos2(c.dir) });
      for (const e of g.hostilesOf(h)) if (inCone(h, e, c.dir, sk.range + bulkOf(e), sk.arc)) g.damage(e, sk.dps * 0.15, h, { skill: true, burn: true });
    }
    walkSlowly(g, h, dt, sk.slow);
    if (g.t >= c.until) { h.channel = null; recover(g, h, 0.15, 'AimRelease'); }
    return true;
  }
  return false;
}
/* A channeling hero may still shuffle along its path, much slower. */
function walkSlowly(g: Game, h: Hero, dt: number, slow: number) {
  if (!h.path.length) return;
  const wp = h.path[0]!, dx = wp.x - h.pos.x, dz = wp.z - h.pos.z, d = Math.hypot(dx, dz), step = h.def.speed * (1 - slow) * dt;
  if (d <= step) { h.pos.set(wp.x, 0, wp.z); h.path.shift(); } else { h.pos.x += dx / d * step; h.pos.z += dz / d * step; }
}
